// Revised App.js implementing:
// - Side panel "book-style" (no modal) anchored right (desktop) / bottom (mobile)
// - Single-selection behavior
// - Powerful search by Nomenclatura & ID
// - Responsive UI with CSS classes

import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { MapContainer, useMap, GeoJSON, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./App.css";

// Components
import SidePanel from './components/SidePanel';
import LayerControls from './components/LayerControls';
import LitoChat from './components/LitoChat';
import MapLayers from './components/MapLayers';

// Services
import { findMatchingParcel, fetchRelatedCommerces, fetchChildParcels } from './services/geoService';
import { getFeatures, cqlString, isAbortError, userMessage, LAYERS } from './services/wfsClient';
import { isPointInFeature } from './services/mapHelpers';
import { CENTRAL_CHAT_KEY, litoMessage, litoWhatsAppUrl, openLitoChatInNewTab } from './services/centralChat';
import { logInteraction } from './utils/utils';

// Constants
const DEFAULT_POSITION = [-31.58241, -68.54243];
const DEFAULT_ZOOM = 19;
const EMPTY_SELECTION = { parcel: null, commerces: [], siblings: [], preferredTab: null };
const NOTICE_DURATION_MS = 5000;
const GEOLOCATION_ERRORS = { 1: 'no se dio permiso de ubicación', 2: 'la ubicación no está disponible', 3: 'se agotó el tiempo de espera' };

/* Misma nomenclatura y mismo titular = misma página (evita mostrar dos veces al mismo dueño) */
const pageKey = (f) => { const p = f.properties || {}; return `${p.Nomenclatura}|${p.Nombre}`; };

/* Zoom handler */
function ZoomHandler() {
  const map = useMap();
  useEffect(() => {
    const f = () => { const osm = document.querySelector('.osm-tiles'); if (osm) osm.style.opacity = map.getZoom() > 19 ? '0.5' : '1'; };
    map.on('zoomend', f);
    return () => map.off('zoomend', f);
  }, [map]);
  return null;
}

/* MapClickHandler: triggers onSpatialSearch on map click */
function MapClickHandler({ onSpatialSearch }) {
  const map = useMap();
  useEffect(() => {
    const onClick = (e) => {
      onSpatialSearch(e.latlng.lat, e.latlng.lng);
    };
    map.on('click', onClick);
    return () => map.off('click', onClick);
  }, [map, onSpatialSearch]);
  return null;
}


/* Main App component */
function App() {
  const [labelsOn, setLabelsOn] = useState(true);
  const [layersVisible, setLayersVisible] = useState({ Parcelario: true, Límites: true, Comercios: true });
  const [baseMap, setBaseMap] = useState('osm');

  // Selection state: se reemplaza entero en cada búsqueda
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const { parcel: selectedParcel, commerces: selectedCommerces, siblings, preferredTab } = selection;
  const [childFeatures, setChildFeatures] = useState([]);
  const [panelIndex, setPanelIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [litoChatMessage, setLitoChatMessage] = useState(null);
  const closeLitoChat = useCallback(() => setLitoChatMessage(null), []);
  // With central-chat disabled (no REACT_APP_CENTRAL_CHAT_KEY) the button goes straight to WhatsApp.
  // Otherwise: on mobile the chat opens full page in a new tab; on desktop (or if the tab is blocked) in the floating panel
  const handleConsultarLito = useCallback((code) => {
    if (!CENTRAL_CHAT_KEY) {
      window.open(litoWhatsAppUrl(litoMessage(code)), '_blank', 'noopener,noreferrer');
      return;
    }
    if (window.matchMedia('(max-width: 768px)').matches && openLitoChatInNewTab(code)) return;
    setLitoChatMessage(litoMessage(code));
  }, []);
  const [userLocation, setUserLocation] = useState(null);

  const mapRef = useRef(null);
  const searchRef = useRef(null); // AbortController de la búsqueda en curso
  const pendingSearchRef = useRef(null); // búsqueda diferida por dirección

  const notify = useCallback((text) => setNotice({ text }), []);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), NOTICE_DURATION_MS);
    return () => clearTimeout(t);
  }, [notice]);

  const handleLocateMe = () => {
    if (!('geolocation' in navigator)) {
      notify('Tu navegador no soporta geolocalización.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation([latitude, longitude]);
        if (mapRef.current) {
          mapRef.current.flyTo([latitude, longitude], 18, { duration: 1.5 });
        }
      },
      (error) => {
        notify(`No se pudo obtener tu ubicación: ${GEOLOCATION_ERRORS[error.code] || error.message}.`);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const onZoom = useCallback((feat) => {
    try {
      if (!feat || !feat.geometry || !mapRef.current) return;
      const geom = feat.geometry; let coords = [];
      if (geom.type === 'Polygon') coords = geom.coordinates[0].map(c => [c[1], c[0]]);
      else if (geom.type === 'MultiPolygon') coords = geom.coordinates[0][0].map(c => [c[1], c[0]]);
      else if (geom.type === 'Point') coords = [[geom.coordinates[1], geom.coordinates[0]]];
      if (coords.length === 1) {
        // Para punto enviamos una vista un poco más arriba si es móvil (muy aproximado)
        const isMobile = window.innerWidth <= 768;
        const latOffset = isMobile ? -0.0003 : 0;
        mapRef.current.setView([coords[0][0] + latOffset, coords[0][1]], Math.max(18, mapRef.current.getZoom()));
      }
      else if (coords.length > 1) {
        // Detectar si es pantalla móvil para dar más margen inferior y evitar que el panel tape
        const isMobile = window.innerWidth <= 768;
        const paddingOptions = isMobile
          ? { maxZoom: 20, paddingTopLeft: [30, 30], paddingBottomRight: [30, window.innerHeight * 0.55] }
          : { maxZoom: 20, padding: [30, 30] };

        mapRef.current.fitBounds(coords, paddingOptions);
      }
    } catch (err) { console.error('onZoom', err); }
  }, []);

  /* Corre una búsqueda cancelando la anterior, así una respuesta vieja nunca pisa a la nueva.
     search(signal) devuelve { selection, zoomTo } o { notFound: 'mensaje' } */
  const runSearch = useCallback(async (search) => {
    clearTimeout(pendingSearchRef.current);
    if (searchRef.current) searchRef.current.abort();
    const controller = new AbortController();
    searchRef.current = controller;
    setIsLoading(true);
    try {
      const result = await search(controller.signal);
      if (controller.signal.aborted) return;
      if (result.notFound) { notify(result.notFound); return; }
      setSelection({ ...EMPTY_SELECTION, ...result.selection });
      if (result.zoomTo) setTimeout(() => { if (searchRef.current === controller) onZoom(result.zoomTo); }, 300);
    } catch (err) {
      if (isAbortError(err)) return;
      console.error('[búsqueda]', err);
      notify(userMessage(err));
    } finally {
      if (searchRef.current === controller) setIsLoading(false);
    }
  }, [notify, onZoom]);

  const handleSpatialSearch = useCallback((lat, lng) => runSearch(async (signal) => {
    const buffer = 0.0001;
    const candidates = await getFeatures(LAYERS.parcelas, { bbox: [lng - buffer, lat - buffer, lng + buffer, lat + buffer], maxFeatures: 10, signal });
    const parcel = candidates.find(f => isPointInFeature(lat, lng, f)) || null;
    const commerces = parcel ? await fetchRelatedCommerces(parcel, { signal }) : [];
    return { selection: { parcel, commerces } };
  }), [runSearch]);

  const handleAddressSelect = useCallback((lat, lng) => {
    if (!mapRef.current) return;
    mapRef.current.flyTo([lat, lng], 19, { duration: 1.5 });
    // Short delay to let the map fly before searching (se cancela si hay otra búsqueda antes)
    clearTimeout(pendingSearchRef.current);
    pendingSearchRef.current = setTimeout(() => handleSpatialSearch(lat, lng), 1500);
  }, [handleSpatialSearch]);

  const handleSearchSelect = useCallback((nomen) => {
    if (!nomen) return;
    runSearch(async (signal) => {
      const [parcel] = await getFeatures(LAYERS.parcelas, { cql: `Nomenclatura = ${cqlString(nomen)}`, maxFeatures: 1, signal });
      if (!parcel) return { notFound: `No se encontró la nomenclatura ${nomen}.` };
      const commerces = await fetchRelatedCommerces(parcel, { strict: true, signal }); // Strict Mode: ID Only
      return { selection: { parcel, commerces }, zoomTo: parcel };
    });
  }, [runSearch]);

  const handleIdContribuyenteSelect = useCallback((id) => {
    if (!id) return;
    runSearch(async (signal) => {
      // 1. Check Parcel by ID. Si hay varias (codominios) la primera es la principal y el resto se suman como páginas
      const parcels = await getFeatures(LAYERS.parcelas, { cql: `id_contribuyente = ${cqlString(id)}`, maxFeatures: 50, signal });
      if (parcels.length > 0) {
        const [parcel, ...parcelSiblings] = parcels;
        const commerces = await fetchRelatedCommerces(parcel, { strict: true, signal }); // Strict Mode: ID Only
        return { selection: { parcel, siblings: parcelSiblings, commerces, preferredTab: 'inmueble' }, zoomTo: parcel };
      }

      // 2. Check Commerce by ID -> Get Parcel matching it
      const [commerce] = await getFeatures(LAYERS.comercios, { cql: `id_contribuyente_comercio = ${cqlString(id)}`, maxFeatures: 1, signal });
      if (commerce) {
        const parcel = await findMatchingParcel(commerce, signal);
        return { selection: { parcel, commerces: [commerce], preferredTab: 'comercio' }, zoomTo: commerce };
      }

      return { notFound: `No se encontró el ID de contribuyente ${id}.` };
    });
  }, [runSearch]);

  const closeSelection = useCallback(() => setSelection(EMPTY_SELECTION), []);

  // Parcelas hijas / codominios de la seleccionada. Se cancela si la selección cambia antes de que respondan.
  useEffect(() => {
    setChildFeatures(prev => (prev.length ? [] : prev));
    if (!selectedParcel) return;
    const controller = new AbortController();
    fetchChildParcels(selectedParcel, controller.signal)
      .then(setChildFeatures)
      .catch(err => { if (!isAbortError(err)) console.error('[fetchChildParcels]', err); });
    return () => controller.abort();
  }, [selectedParcel]);

  // Páginas del panel: la parcela seleccionada + codominios por ID + hijas, sin repetir
  const panelPages = useMemo(() => {
    if (!selectedParcel) return [];
    const seen = new Set([pageKey(selectedParcel)]);
    const extra = [...siblings, ...childFeatures].filter(f => {
      const key = pageKey(f);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return [selectedParcel, ...extra];
  }, [selectedParcel, siblings, childFeatures]);

  useEffect(() => { setPanelIndex(0); }, [panelPages]);

  const panelOpen = panelPages.length > 0 || selectedCommerces.length > 0;
  const extraPages = panelPages.slice(1);

  // Ingreso por link: ?id=123 o QR con el ID numérico como último segmento (/.../12345)
  useEffect(() => {
    const idParam = new URLSearchParams(window.location.search).get('id');
    if (idParam) {
      handleIdContribuyenteSelect(idParam);
      logInteraction(idParam, 'ingreso_link_param', window.location.href);
      return;
    }

    const last = window.location.pathname.split('/').filter(Boolean).pop();
    if (last && /^\d+$/.test(last)) {
      handleIdContribuyenteSelect(last);
      logInteraction(last, 'ingreso_link_qr', window.location.href);
    }
  }, [handleIdContribuyenteSelect]);

  return (
    <div className="map-container">
      <SidePanel
        open={panelOpen}
        parcelPages={panelPages}
        activeParcelIndex={panelIndex}
        setActiveParcelIndex={setPanelIndex}
        commerceDataList={selectedCommerces}
        preferredTab={preferredTab}
        onClose={closeSelection}
        onZoom={onZoom}
        onConsultarLito={handleConsultarLito}
        onNotify={notify}
      />

      <LitoChat message={litoChatMessage} onClose={closeLitoChat} panelOpen={panelOpen} />

      <LayerControls
        labelsOn={labelsOn}
        setLabelsOn={setLabelsOn}
        layersVisible={layersVisible}
        setLayersVisible={setLayersVisible}
        baseMap={baseMap}
        setBaseMap={setBaseMap}
        onSearchSelect={handleSearchSelect}
        onIdSelect={handleIdContribuyenteSelect}
        onAddressSelect={handleAddressSelect}
        onLocateMe={handleLocateMe}
        onNotify={notify}
      />

      <button
        className={`floating-locate-btn ${panelOpen ? 'panel-open' : ''}`}
        onClick={handleLocateMe}
        title="Ir a mi ubicación"
      >
        📍
      </button>

      <MapContainer ref={mapRef} center={DEFAULT_POSITION} zoom={DEFAULT_ZOOM} minZoom={2} maxZoom={22} style={{ height: '100%', width: '100%' }} preferCanvas={true}>
        <ZoomHandler />
        <MapClickHandler onSpatialSearch={handleSpatialSearch} />

        {selectedParcel && (
          <GeoJSON
            key={selectedParcel.id || (selectedParcel.properties && selectedParcel.properties.id_contribuyente) || 'parcel-selected'}
            data={selectedParcel}
            style={() => ({ color: '#ff0000', weight: 4, fillOpacity: 0.18, fillColor: '#ffcccc' })}
          />
        )}

        {selectedCommerces.map((com, index) => (
          <GeoJSON
            key={com.id || (com.properties && com.properties.id_contribuyente_comercio) || `com-${index}`}
            data={com}
            style={() => ({ color: '#ff8800', weight: 4, fillOpacity: 0.2, fillColor: '#ffcc00' })}
            pointToLayer={(feature, latlng) => {
              return L.circleMarker(latlng, { radius: 8, color: '#ff8800', fillColor: '#ffcc00', fillOpacity: 0.8, weight: 2 });
            }}
          />
        ))}

        {extraPages.length > 0 && (
          <GeoJSON
            key={`${pageKey(selectedParcel)}-${extraPages.length}`}
            data={{ type: 'FeatureCollection', features: extraPages }}
            style={() => ({ color: '#0066ff', weight: 2, fillOpacity: 0.06, fillColor: '#ccddff' })}
          />
        )}

        {/* User location marker */}
        {userLocation && (
          <CircleMarker
            center={userLocation}
            radius={10}
            pathOptions={{ fillColor: '#4285F4', color: '#ffffff', weight: 3, opacity: 1, fillOpacity: 0.8 }}
          />
        )}

        <MapLayers baseMap={baseMap} layersVisible={layersVisible} labelsOn={labelsOn} />
      </MapContainer>

      {/* Global Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner-container">
            <div className="spinner"></div>
            <div className="loading-text">Buscando...</div>
          </div>
        </div>
      )}

      {notice && (
        <div className="app-notice" role="status" onClick={() => setNotice(null)}>
          {notice.text}
        </div>
      )}
    </div>
  );
}

export default App;
